/**
 * "Gate Impact" table for a finished manual simulation: how many trades
 * each gate removed and each override added.
 *
 * Two layers of numbers:
 *  - Funnel counts (always present, free): from `result_summary.gate_impact`,
 *    computed by the engine during the run. "Fired" = windows where the
 *    gate failed on at least one tick; "Credited" = windows where it was
 *    the last feature gate still blocking. Both are conditional on the
 *    other enabled gates and on evaluation order (the engine short-circuits),
 *    so they describe how active a gate is IN THIS CONFIG, not its value.
 *  - Exact (opt-in buttons): `POST /gate-ablation?mode=` replays the run
 *    once per enabled feature and reports the deltas. `leave_one_out`
 *    switches each feature off against the full config (what the feature
 *    contributes at the margin IN THIS CONFIG); `standalone` switches
 *    every feature off and adds each one back alone against that core
 *    run (the feature's edge independent of the other gates). These are
 *    the only P/L figures shown: the engine also records a per-window
 *    would-have-been estimate, but it depends on the gates behind the
 *    credited one never getting a vote and is biased toward winners, so
 *    it is deliberately not rendered as money.
 */

import type { CSSProperties, ReactNode } from "react";
import { Badge, Button, Card, Group, Stack, Table, Text, Title, Tooltip } from "@mantine/core";
import { IconCalculator, IconStack2 } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useMutation } from "@tanstack/react-query";

import { api } from "../api/client";
import type {
  SimulationGateAblation,
  SimulationGateAblationMode,
  SimulationGateImpact,
  SimulationGateImpactRow,
} from "../api/schemas";
import { EFFECT_LABEL, resolveFeatureMeta } from "../lib/featureMeta";

export interface GateImpactCardProps {
  runId: string;
  impact: SimulationGateImpact | null;
  /** Feature key (enabledKey / metaKey) -> card title, for row labels. */
  featureTitles: Record<string, string>;
  /** False while the run is still in progress (ablation needs a finished run). */
  canAblate: boolean;
}

type ExactRow = SimulationGateAblation["rows"][number];

const NOWRAP: CSSProperties = { whiteSpace: "nowrap" };
const NUM: CSSProperties = { ...NOWRAP, fontVariantNumeric: "tabular-nums", textAlign: "right" };
const MONO: CSSProperties = { fontFamily: "var(--mantine-font-family-monospace)" };

function extractErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function featureLabel(feature: string, titles: Record<string, string>): string {
  if (feature === "core") return "Config & Core Entry";
  return titles[feature] ?? feature;
}

function fmtMoney(v: string | number): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  const sign = n > 0 ? "+" : n < 0 ? "−" : "";
  return `${sign}$${Math.abs(n).toFixed(2)}`;
}

function fmtDelta(v: number): string {
  if (v > 0) return `+${v}`;
  if (v < 0) return `−${Math.abs(v)}`;
  return "0";
}

function colorFor(n: number): string | undefined {
  if (n > 0) return "var(--mantine-color-profit-5, var(--mantine-color-green-6))";
  if (n < 0) return "var(--mantine-color-loss-5, var(--mantine-color-red-6))";
  return undefined;
}

function Money({ value }: { value: string | number }) {
  const n = Number(value);
  return <Text component="span" size="sm" style={{ ...MONO, ...NOWRAP, color: colorFor(n) }}>{fmtMoney(value)}</Text>;
}

function Delta({ value }: { value: number }) {
  return <Text component="span" size="sm" style={{ ...MONO, ...NOWRAP, color: colorFor(value) }}>{fmtDelta(value)}</Text>;
}

function Plain({ children }: { children: ReactNode }) {
  return <Text component="span" size="sm" style={{ ...MONO, ...NOWRAP }}>{children}</Text>;
}

function Th({ children, help, align = "right" }: { children: ReactNode; help?: string; align?: "left" | "right" }) {
  const label = <span style={{ ...NOWRAP, cursor: help ? "help" : undefined }}>{children}</span>;
  return (
    <Table.Th style={{ ...NOWRAP, textAlign: align }}>
      {help ? <Tooltip label={help} multiline w={320} withArrow>{label}</Tooltip> : label}
    </Table.Th>
  );
}

function FeatureCell({ feature, titles }: { feature: string; titles: Record<string, string> }) {
  const meta = resolveFeatureMeta(feature);
  const effect = meta ? EFFECT_LABEL[meta.effect] : null;
  return (
    <Table.Td style={{ minWidth: 220 }}>
      <Stack gap={2}>
        <Text size="sm" fw={500} lh={1.25}>{featureLabel(feature, titles)}</Text>
        {effect ? <Badge size="xs" variant="light" color={effect.color} w="fit-content">{effect.label}</Badge> : null}
      </Stack>
    </Table.Td>
  );
}

function ExactCells({ row }: { row: ExactRow | undefined }) {
  if (!row) {
    return (
      <>
        <Table.Td style={NUM} c="dimmed">—</Table.Td>
        <Table.Td style={NUM} c="dimmed">—</Table.Td>
        <Table.Td style={NUM} c="dimmed">—</Table.Td>
      </>
    );
  }
  return (
    <>
      <Table.Td style={NUM}><Delta value={row.delta_orders} /></Table.Td>
      <Table.Td style={NUM}><Delta value={row.delta_wins} /><Plain> / </Plain><Delta value={row.delta_losses} /></Table.Td>
      <Table.Td style={NUM}><Money value={row.delta_pnl_usdc} /></Table.Td>
    </>
  );
}

const EXACT_HEADERS = (
  <>
    <Th help="Exact: orders in this run minus orders with the feature switched off (everything else unchanged). Negative = the feature removed that many trades; positive = it added them.">Δ Orders</Th>
    <Th help="Exact: change in wins / losses attributable to the feature (same sign convention as Δ Orders).">Δ W / L</Th>
    <Th help="Exact: this run's net P/L minus net P/L with the feature switched off. Green = the feature improved P/L in this config; red = you would be better off without it.">Δ P/L</Th>
  </>
);

const STANDALONE_HEADERS = (
  <>
    <Th help="Orders placed by the core-only config (every feature off) → orders with just this feature added back.">Orders (core → alone)</Th>
    <Th help="Standalone: orders with core + only this feature, minus orders with core alone. Negative = the feature removed that many trades from the core stream; positive = it added them.">Δ Orders</Th>
    <Th help="Standalone: change in wins / losses versus the core-only run (same sign convention as Δ Orders).">Δ W / L</Th>
    <Th help="Standalone: net P/L with core + only this feature, minus net P/L of the core-only run. Green = the feature has edge on its own; red = alone it hurts. Independent of which other features are enabled.">Δ P/L</Th>
  </>
);

function AblationBadge({ data }: { data: SimulationGateAblation }) {
  const engine = data.engine === "python" ? "Python" : "TS";
  const label = data.mode === "standalone"
    ? `Replayed on the ${engine} engine; the core-only config (every feature off) was run first and each feature was added back alone against it.`
    : data.baseline_source === "saved"
      ? `Replayed on the ${engine} engine; deltas are against this run's saved summary.`
      : `Replayed on the ${engine} engine; the unmodified config was re-run first so every delta compares same-engine numbers.`;
  return (
    <Tooltip label={label} multiline w={320} withArrow>
      <Badge variant="light" color={data.mode === "standalone" ? "grape" : "teal"} style={{ cursor: "help" }}>
        {data.mode === "standalone" ? "standalone" : "exact"} · {data.engine} · {data.rows.length} replays · {data.elapsed_seconds.toFixed(0)}s
      </Badge>
    </Tooltip>
  );
}

function useAblation(runId: string, mode: SimulationGateAblationMode) {
  return useMutation({
    mutationFn: () => api.simulations.gateAblation(runId, mode),
    onError: (error) =>
      notifications.show({
        title: mode === "standalone" ? "Standalone gate impact failed" : "Exact gate impact failed",
        message: extractErrorMessage(error),
        color: "red",
      }),
  });
}

export function GateImpactCard({ runId, impact, featureTitles, canAblate }: GateImpactCardProps) {
  const ablation = useAblation(runId, "leave_one_out");
  const standalone = useAblation(runId, "standalone");
  const exact = new Map<string, ExactRow>((ablation.data?.rows ?? []).map((r) => [r.feature, r]));
  const hasExact = exact.size > 0;
  const standaloneRows = [...(standalone.data?.rows ?? [])].sort((a, b) => Number(b.delta_pnl_usdc) - Number(a.delta_pnl_usdc));
  const coreBaseline = standalone.data?.baseline;

  const funnelRows = impact?.rows ?? [];
  const core = funnelRows.find((r) => r.feature === "core");
  // Order by exact Δ P/L (most valuable gate first) once available, else by
  // how many windows the gate was credited with.
  const sortKey = (r: SimulationGateImpactRow) => {
    const e = exact.get(r.feature);
    return e ? Number(e.delta_pnl_usdc) : r.windows / 1e6;
  };
  const gateRows = funnelRows.filter((r) => r.kind === "gate" && r.feature !== "core").sort((a, b) => sortKey(b) - sortKey(a));
  const overrideRows = funnelRows.filter((r) => r.kind === "override").sort((a, b) => sortKey(b) - sortKey(a));
  const funnelFeatures = new Set(funnelRows.map((r) => r.feature));
  // Features the exact replay covered that never showed up in the funnel
  // (exits, sizing, gates that blocked nothing): exact deltas only.
  const exactOnlyRows = (ablation.data?.rows ?? []).filter((r) => !funnelFeatures.has(r.feature))
    .sort((a, b) => Number(b.delta_pnl_usdc) - Number(a.delta_pnl_usdc));

  const blockedByGate = gateRows.reduce((s, r) => s + r.windows, 0);

  const exactButton = canAblate && !ablation.data ? (
    <Tooltip label="Re-runs the simulation once for every enabled feature with that feature turned off and diffs the results. Cost: about (enabled features) × one run." multiline w={320} withArrow>
      <Button size="xs" variant="light" leftSection={<IconCalculator size={14} />}
        loading={ablation.isPending} onClick={() => ablation.mutate()}>
        Compute exact impact
      </Button>
    </Tooltip>
  ) : null;
  const standaloneButton = canAblate && !standalone.data ? (
    <Tooltip label="Switches every feature off to get a core-only baseline, then re-runs once per enabled feature with only that feature added back. Each row is independent of the other gates. Cost: about (enabled features + 1) × one run." multiline w={320} withArrow>
      <Button size="xs" variant="light" color="grape" leftSection={<IconStack2 size={14} />}
        loading={standalone.isPending} onClick={() => standalone.mutate()}>
        Compute standalone impact
      </Button>
    </Tooltip>
  ) : null;
  const buttons = exactButton || standaloneButton ? <>{exactButton}{standaloneButton}</> : null;

  const standaloneSection = standalone.data ? (
    <Stack gap={6}>
      <Text size="xs" fw={600} tt="uppercase" c="dimmed">Standalone — each feature alone on the core rules</Text>
      {coreBaseline ? (
        <Text size="xs" c="dimmed" style={NOWRAP}>
          Core alone (every feature off): <b>{coreBaseline.windows_with_bet}</b> orders,{" "}
          <b>{coreBaseline.wins}</b> / <b>{coreBaseline.losses_including_stop_losses}</b> W / L,{" "}
          <Money value={coreBaseline.pnl_usdc} />
        </Text>
      ) : null}
      {standaloneRows.length === 0 ? (
        <Text size="sm" c="dimmed">No ablatable feature is enabled in this config.</Text>
      ) : (
        <Table.ScrollContainer minWidth={640}>
          <Table withTableBorder striped highlightOnHover verticalSpacing="xs" horizontalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Th align="left">Feature</Th>
                {STANDALONE_HEADERS}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {standaloneRows.map((r) => (
                <Table.Tr key={r.feature}>
                  <FeatureCell feature={r.feature} titles={featureTitles} />
                  <Table.Td style={NUM}><Plain>{r.without.windows_with_bet} → {r.with_feature.windows_with_bet}</Plain></Table.Td>
                  <ExactCells row={r} />
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Stack>
  ) : null;

  if (funnelRows.length === 0 && !ablation.data) {
    return (
      <Card withBorder>
        <Stack gap="xs">
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Title order={5}>Gate Impact</Title>
            {standalone.data ? <AblationBadge data={standalone.data} /> : null}
          </Group>
          <Text size="sm" c="dimmed">
            {impact
              ? "No gate blocked a window and no override placed an order in this run."
              : "This run predates gate attribution (or ran on an engine path that does not record it). Re-run the simulation to populate the table; the exact replays still work."}
          </Text>
          {buttons ? <Group gap="xs">{buttons}</Group> : null}
          {standaloneSection}
        </Stack>
      </Card>
    );
  }

  const renderGateRow = (r: SimulationGateImpactRow) => (
    <Table.Tr key={r.feature}>
      <FeatureCell feature={r.feature} titles={featureTitles} />
      <Table.Td style={NUM}><Plain>{r.windows_any_tick}</Plain></Table.Td>
      <Table.Td style={NUM}><Plain>{r.windows}</Plain></Table.Td>
      <ExactCells row={exact.get(r.feature)} />
    </Table.Tr>
  );

  const renderOverrideRow = (r: SimulationGateImpactRow) => (
    <Table.Tr key={r.feature}>
      <FeatureCell feature={r.feature} titles={featureTitles} />
      <Table.Td style={NUM}><Plain>{r.windows}</Plain></Table.Td>
      <Table.Td style={NUM}>
        <Plain>{r.wins} / {r.losses}</Plain>
        {r.unresolved > 0 ? <Text component="span" size="xs" c="dimmed" style={NOWRAP}> (+{r.unresolved} ?)</Text> : null}
      </Table.Td>
      <Table.Td style={NUM}><Money value={r.pnl_usdc} /></Table.Td>
      <ExactCells row={exact.get(r.feature)} />
    </Table.Tr>
  );

  const renderExactOnlyRow = (r: ExactRow) => (
    <Table.Tr key={r.feature}>
      <FeatureCell feature={r.feature} titles={featureTitles} />
      <ExactCells row={r} />
    </Table.Tr>
  );

  return (
    <Card withBorder>
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={4}>
            <Title order={5}>Gate Impact</Title>
            <Text size="xs" c="dimmed">
              How many trades each gate removed and each override added. <b>Fired</b> / <b>Credited</b> are free
              activity counts from this run; the <b>Δ</b> columns are exact and come from re-running the simulation
              with each feature switched off. <b>Standalone</b> re-runs each feature alone on top of the core rules.
            </Text>
          </Stack>
          <Group gap="xs" wrap="wrap" justify="flex-end">
            {ablation.data ? <AblationBadge data={ablation.data} /> : null}
            {standalone.data ? <AblationBadge data={standalone.data} /> : null}
            {buttons}
          </Group>
        </Group>

        {core || gateRows.length > 0 ? (
          <Group gap="lg" wrap="wrap">
            <Text size="xs" c="dimmed" style={NOWRAP}>
              Never had a candidate (odds band / no favourite / cutoffs): <b>{core?.windows ?? 0}</b> windows
            </Text>
            <Text size="xs" c="dimmed" style={NOWRAP}>
              Had a candidate but a gate removed it: <b>{blockedByGate}</b> windows
            </Text>
          </Group>
        ) : null}

        {gateRows.length > 0 ? (
          <Stack gap={6}>
            <Text size="xs" fw={600} tt="uppercase" c="dimmed">Entry gates — removed trades</Text>
            <Table.ScrollContainer minWidth={640}>
              <Table withTableBorder striped highlightOnHover verticalSpacing="xs" horizontalSpacing="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Th align="left">Gate</Th>
                    <Th help="Windows where this gate failed on at least one evaluated tick. Overlapping: a window can count under several gates. Conditional on the other gates and on evaluation order (a later gate only sees ticks the earlier ones passed), so this measures activity in this config, not value.">Fired</Th>
                    <Th help="Windows credited to this gate: it was the last feature gate still blocking when the window ran out. Each no-order window is credited to exactly one gate, so these sum to the 'gate removed it' total. Same conditionality as Fired.">Credited</Th>
                    {EXACT_HEADERS}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>{gateRows.map(renderGateRow)}</Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Stack>
        ) : null}

        {overrideRows.length > 0 ? (
          <Stack gap={6}>
            <Text size="xs" fw={600} tt="uppercase" c="dimmed">Overrides — added trades</Text>
            <Table.ScrollContainer minWidth={620}>
              <Table withTableBorder striped highlightOnHover verticalSpacing="xs" horizontalSpacing="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Th align="left">Override</Th>
                    <Th help="Orders placed while this override was active on the placing tick (actual, not estimated).">Added</Th>
                    <Th help="Actual wins / losses of those orders.">W / L</Th>
                    <Th help="Actual settled P/L of those orders. Note an override can also displace a trade the core rules would have taken anyway; Δ columns account for that.">P/L</Th>
                    {EXACT_HEADERS}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>{overrideRows.map(renderOverrideRow)}</Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Stack>
        ) : null}

        {exactOnlyRows.length > 0 ? (
          <Stack gap={6}>
            <Text size="xs" fw={600} tt="uppercase" c="dimmed">Other enabled features — exact only</Text>
            <Table.ScrollContainer minWidth={480}>
              <Table withTableBorder striped highlightOnHover verticalSpacing="xs" horizontalSpacing="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Th align="left">Feature</Th>
                    {EXACT_HEADERS}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>{exactOnlyRows.map(renderExactOnlyRow)}</Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Stack>
        ) : null}

        {standaloneSection}

        <Text size="xs" c="dimmed">
          <b>Fired</b> and <b>Credited</b> depend on which other gates are enabled and in what order they run; they
          tell you how busy a gate is, not what it is worth. Only the <b>Δ</b> columns measure value: baseline minus the
          run without the feature, with every other gate, exit and sizing rule still applied.
          {hasExact ? " Δ values do not sum to the total because gates overlap." : ""}
          {standalone.data
            ? " Standalone Δ values are independent of the other features but ignore interactions: a feature can be worthless alone yet essential in combination (or the reverse), so read both tables together."
            : ""}
          {" "}Picking gates purely by historical P/L deltas overfits the past; confirm on another date range.
        </Text>
      </Stack>
    </Card>
  );
}
